import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { MonthYearPicker } from "../components/Filters";
import { MONTHS, formatRupiah } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, TrendingDown, Settings2, Loader2 } from "lucide-react";

function Line({ label, value, bold }) {
  return (
    <div className={`flex justify-between py-2 ${bold ? "font-nunito font-bold text-gtsnavy border-t" : "text-sm"}`}>
      <span>{label}</span><span>{formatRupiah(value)}</span>
    </div>
  );
}

export default function Expenses() {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(8);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [setDlg, setSetDlg] = useState(false);
  const [form, setForm] = useState({ category: "", description: "", amount: "", date: "" });
  const [cfg, setCfg] = useState({ thr_percentage: 20, maintenance_dpo: 300000 });

  const load = () => {
    api.get(`/finance/summary?year=${year}&month=${month}`).then((r) => setSummary(r.data));
    api.get(`/expenses?year=${year}&month=${month}`).then((r) => setExpenses(r.data));
  };
  useEffect(() => { load(); }, [year, month]);
  useEffect(() => { api.get("/settings/finance").then((r) => setCfg({ thr_percentage: r.data.thr_percentage ?? 20, maintenance_dpo: r.data.maintenance_dpo ?? 300000 })); }, []);

  const addExp = async () => {
    const amt = parseInt(form.amount, 10);
    if (!form.category || isNaN(amt) || amt < 0) { toast.error("Isi kategori & nominal valid"); return; }
    const body = { year, month, category: form.category, description: form.description || null, amount: amt, date: form.date || null };
    try {
      if (editingId) await api.put(`/expenses/${editingId}`, body);
      else await api.post("/expenses", body);
      toast.success(editingId ? "Pengeluaran diperbarui" : "Pengeluaran ditambahkan"); setDialog(false); setEditingId(null);
      setForm({ category: "", description: "", amount: "", date: "" }); load();
    } catch (e) { toast.error("Gagal"); }
  };

  const openNewExp = () => { setEditingId(null); setForm({ category: "", description: "", amount: "", date: "" }); setDialog(true); };
  const openEditExp = (e) => { setEditingId(e.id); setForm({ category: e.category, description: e.description || "", amount: String(e.amount), date: e.date || "" }); setDialog(true); };

  const delExp = async (id) => { try { await api.delete(`/expenses/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error("Gagal"); } };

  const saveCfg = async () => {
    try {
      await api.put("/settings/finance", { thr_percentage: Number(cfg.thr_percentage), maintenance_dpo: Number(cfg.maintenance_dpo) });
      toast.success("Pengaturan disimpan"); setSetDlg(false); load();
    } catch (e) { toast.error("Gagal"); }
  };

  const saldo = summary?.saldo_bersih ?? 0;
  const saldoColor = saldo > 0 ? "from-emerald-500 to-emerald-600" : saldo < 0 ? "from-rose-500 to-rose-600" : "from-gtsorange to-gtsorange-light";

  return (
    <div className="space-y-5" data-testid="expenses-page">
      <Toaster position="top-center" richColors />
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">Pengeluaran GTS</h2>
          <p className="text-sm text-muted-foreground">{MONTHS[month - 1]} {year}</p>
        </div>
        <div className="flex gap-2">
          <MonthYearPicker year={year} month={month} setYear={setYear} setMonth={setMonth} />
          <Button variant="outline" onClick={() => setSetDlg(true)} className="rounded-full gap-1" data-testid="finance-settings-btn"><Settings2 className="w-4 h-4" /> Pengaturan</Button>
        </div>
      </div>

      {!summary ? <div className="p-16 flex justify-center"><Loader2 className="w-7 h-7 text-gtsorange animate-spin" /></div> : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-6 bg-white" data-testid="expense-breakdown">
              <div className="flex items-center gap-2 mb-3"><TrendingDown className="w-5 h-5 text-gtsorange" /><h3 className="font-nunito font-bold text-gtsnavy">Rincian Pengeluaran</h3></div>
              <Line label="Total Gaji 6 Karyawan" value={summary.total_gaji} />
              <Line label={`Tabungan THR (${summary.thr_percentage}%)`} value={summary.thr} />
              <Line label="Maintenance & Cadangan DPO" value={summary.maintenance_dpo} />
              <Line label="Pengeluaran Lain-lain" value={summary.expenses_other} />
              <Line label="Total Pengeluaran GTS" value={summary.total_pengeluaran} bold />
            </Card>

            <Card className={`rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-6 bg-gradient-to-br ${saldoColor} text-white flex flex-col justify-center`} data-testid="saldo-card">
              <p className="text-white/80 text-sm">Saldo Bersih GTS Bulan {MONTHS[month - 1]} {year}</p>
              <p className="font-nunito font-black text-4xl mt-2" data-testid="saldo-value">{formatRupiah(saldo)}</p>
              <div className="mt-4 text-sm text-white/80 space-y-1">
                <div className="flex justify-between"><span>Total Penerimaan</span><span>{formatRupiah(summary.total_penerimaan)}</span></div>
                <div className="flex justify-between"><span>Total Pengeluaran</span><span>{formatRupiah(summary.total_pengeluaran)}</span></div>
              </div>
            </Card>
          </div>

          <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="font-nunito font-bold text-gtsnavy">Pengeluaran Lain-lain</h3>
              <Button size="sm" onClick={openNewExp} className="rounded-full bg-gtsorange text-white gap-1" data-testid="add-expense-btn"><Plus className="w-4 h-4" /> Tambah</Button>
            </div>
            <div className="overflow-x-auto gts-scroll">
              <table className="w-full text-sm">
                <thead><tr className="bg-gtsnavy text-white">
                  <th className="text-left px-4 py-3">Tanggal</th><th className="text-left px-4 py-3">Kategori</th>
                  <th className="text-left px-4 py-3">Keterangan</th><th className="text-right px-4 py-3">Nominal</th><th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {(!expenses || expenses.items.length === 0) && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Belum ada.</td></tr>}
                  {expenses?.items.map((e, i) => (
                    <tr key={e.id} className={i % 2 ? "bg-orange-50/30" : ""} data-testid={`expense-row-${e.id}`}>
                      <td className="px-4 py-2.5">{e.date || "-"}</td>
                      <td className="px-4 py-2.5">{e.category}</td>
                      <td className="px-4 py-2.5">{e.description || "-"}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{formatRupiah(e.amount)}</td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button onClick={() => openEditExp(e)} className="text-gtsnavy/60 hover:text-gtsnavy mr-3" data-testid={`edit-expense-${e.id}`}><Pencil className="w-4 h-4 inline" /></button>
                        <button onClick={() => delExp(e.id)} className="text-rose-400 hover:text-rose-600" data-testid={`del-expense-${e.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Edit Pengeluaran" : "Tambah Pengeluaran"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl mt-1" data-testid="expense-category" /></div>
            <div><Label>Keterangan</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nominal</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl mt-1" data-testid="expense-amount" /></div>
              <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={addExp} className="rounded-full bg-gtsnavy text-white" data-testid="save-expense-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={setDlg} onOpenChange={setSetDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pengaturan Keuangan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Persentase Tabungan THR (%)</Label><Input type="number" value={cfg.thr_percentage} onChange={(e) => setCfg({ ...cfg, thr_percentage: e.target.value })} className="rounded-xl mt-1" data-testid="thr-pct-input" /></div>
            <div><Label>Maintenance & Cadangan DPO (Rp)</Label><Input type="number" value={cfg.maintenance_dpo} onChange={(e) => setCfg({ ...cfg, maintenance_dpo: e.target.value })} className="rounded-xl mt-1" data-testid="maintenance-input" /></div>
          </div>
          <DialogFooter><Button onClick={saveCfg} className="rounded-full bg-gtsnavy text-white" data-testid="save-finance-cfg">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
